import dynamic from 'next/dynamic';
import { useRef } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { IDescriptionWithSlidesBlock } from '@features/cms/typescript';
import styles from '@features/cms/components/collections/DescriptionWithSlidesBlock/DescriptionWithSlidesBlock.styles';
import VoxboxWidget from '@features/cms/components/collections/VoxboxWidget/VoxboxWidget';
import Button from '@components/Button/Button';
import { ECarouselType } from '@components/Carousel/typescript';
import Heading from '@components/Heading/Heading';
import scrollToElementById from '@utils/scrollToElementById';
import theme from '@styles/theme';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
import sanitizeHtml from '@utils/sanitizeHtml';
const DynamicFurnitureCarousel = dynamic(() => import('@components/FurnituresCarousel/FurnituresCarousel'), {
  ssr: true
});
type PropsType = {
  data: IDescriptionWithSlidesBlock;
  isFirst?: boolean;
  defaultBackground?: string;
};
const DescriptionWithSlidesBlock = ({
  data,
  isFirst = false,
  defaultBackground = theme.rawColors.neutralWhite
}: PropsType) => {
  const hoverRef = useRef<HTMLElement>(null);
  const {
    voxboxButton,
    slides,
    title,
    description,
    background,
    link,
    descriptionColor,
    code
  } = data;
  const {
    t
  } = useTranslation('common');
  const getLinkText = () => link?.text?.length ? link.text : t('products.all');
  const getLinkTarget = () => link?.properties?.target?.length ? link?.properties?.target : '_self';
  const getDescription = () => <span dangerouslySetInnerHTML={sanitizeHtml(description.replace(/\n/g, '<br>'))} />;
  return <section id={code} sx={styles.section(background || defaultBackground)} ref={hoverRef} data-cy={code} data-testid="description-with-slides-block">
      <div sx={styles.description(descriptionColor)}>
        {title && <Heading h={isFirst ? 1 : 2} headingType={2} mobile>
            {title}
          </Heading>}
        {isFirst && !title ? <Heading h={1}>{getDescription()}</Heading> : <p>{getDescription()}</p>}
        {link.path && <>
            {link.path.slice(0, 1) === '#' ? <Button onClick={() => scrollToElementById(link.path.substring(1))} variant="default" size="small" variantColor="dark" letterCase="lowercase" cy={`${code}-button`} data-testid="description-with-slides-block-button">
                {getLinkText()}
              </Button> : <Link href={link?.path ? prepareURLByCMSLink(link) : '#'} target={getLinkTarget()} scroll={false}>
                <Button variant="default" size="small" variantColor="dark" letterCase="lowercase" data-testid="description-with-slides-block-button" cy={`${code}-button`}>
                  {getLinkText()}
                </Button>
              </Link>}
          </>}
      </div>
      {slides?.length > 0 && <DynamicFurnitureCarousel slides={slides} type={ECarouselType.packshot} />}
      {voxboxButton && <VoxboxWidget hoverRef={hoverRef} variant="dark" />}
    </section>;
};
export default DescriptionWithSlidesBlock;